import React, { useEffect } from 'react';
import { BOTTOM_INDENTS, COLORS } from '@constants';
import { useStore } from '@hooks';
import { Button, Icon, Tooltip, Typography } from '@ui';
import { formatString } from '@utils';
import { Flex } from 'antd';
import { observer } from 'mobx-react';
import { MODE, SETUP_KEYS } from '../../constants';
import { useSetupModal } from '../../hooks/useSetupModal';
import StatusBadge from '../StatusBadge';
import {
    StyledFlex,
    StyledLineAccount,
    StyledLineCampaign,
    StyledStatusAccount,
    StyledStatusCampaign,
    StyledSubtitle,
    StyledTitleWrapper
} from './styled';

const TooltipIcon = ({ tooltipText, showTooltip, marginRight = 0 }) => {
    if (!showTooltip || !tooltipText) {
        return null;
    }

    return (
        <Tooltip title={tooltipText} trigger='hover'>
            <span style={{ cursor: 'help', marginRight: marginRight || undefined }}>
                <Icon size='16px' color={COLORS.$gray40Black} component={() => <Icon.QuestionCircleIcon />} />
            </span>
        </Tooltip>
    );
};

const LineItem = observer(
    ({
        setupKey,
        data,
        setModal,
        modal,
        isManual = true,
        bottomMargin = BOTTOM_INDENTS.S,
        isCollapsible,
        isOpen,
        onToggle,
        variant = MODE.ACCOUNT,
        showTooltip = false,
        tooltipText = '',
        style,
        removeBottomMargin = false,
        onModalClose
    }) => {
        const store = useStore();
        const { openModal, close } = useSetupModal(store, data, modal, setupKey, isManual);

        const onModalOpen = () => {
            if (!setupKey) {
                return;
            }
            if (typeof setModal === 'function') {
                setModal(setupKey);
            }
            openModal(setupKey);
        };

        const StyledLine = variant === MODE.CAMPAIGN ? StyledLineCampaign : StyledLineAccount;
        const StyledStatus = variant === MODE.CAMPAIGN ? StyledStatusCampaign : StyledStatusAccount;

        const marginStyle =
            variant === MODE.CAMPAIGN ? (removeBottomMargin ? {} : BOTTOM_INDENTS.M) : { ...bottomMargin, ...style };

        useEffect(() => {
            if (onModalClose && setupKey === SETUP_KEYS.ANALYTICS_SCRIPT && modal === setupKey) {
                onModalClose(close);
            }
        }, [close, onModalClose, modal, setupKey]);

        return (
            <StyledLine justify='space-between' align='center' style={marginStyle}>
                {variant === MODE.ACCOUNT ? (
                    <StyledTitleWrapper>
                        <TooltipIcon tooltipText={tooltipText} showTooltip={showTooltip} marginRight='4px' />
                        <Typography.Title level={3}>{data.title}</Typography.Title>
                        {data.subtitle && <StyledSubtitle>{data.subtitle}</StyledSubtitle>}
                        {isCollapsible && (
                            <React.Fragment>
                                {isOpen ? (
                                    <Icon.ArrowChevronUpIcon
                                        width='16'
                                        height='16'
                                        color={COLORS.$gray40Black}
                                        strokeWidth='4'
                                        style={{ cursor: 'pointer' }}
                                        onClick={onToggle}
                                        data-e2e={`integrations_${formatString(data.title)}-expand-up_icon`}
                                    />
                                ) : (
                                    <Icon.ArrowChevronDownIcon
                                        width='16'
                                        height='16'
                                        color={COLORS.$gray40Black}
                                        strokeWidth='4'
                                        style={{ cursor: 'pointer' }}
                                        onClick={onToggle}
                                        data-e2e={`integrations_${formatString(data.title)}-expand-down_icon`}
                                    />
                                )}
                            </React.Fragment>
                        )}
                    </StyledTitleWrapper>
                ) : (
                    <Flex align='center' gap={4}>
                        <TooltipIcon tooltipText={tooltipText} showTooltip={showTooltip} />
                        <Typography.Title level={3}>{data.title}</Typography.Title>
                    </Flex>
                )}
                <StyledFlex justify='space-between' align='center'>
                    {data.viewMode ? (
                        <Button data-e2e={`integrations_${setupKey}_btn`} type='link' padding='0' onClick={onModalOpen}>
                            View
                        </Button>
                    ) : (
                        <StyledStatus data-e2e={`integrations_${setupKey}_status`}>
                            {variant === MODE.CAMPAIGN ? (
                                <React.Fragment>
                                    <StatusBadge setupKey={setupKey} status={data.status} variant={variant} />
                                    <Button
                                        data-e2e={`integrations_${setupKey}-connection_btn`}
                                        type='link'
                                        padding='0'
                                        onClick={onModalOpen}
                                    >
                                        {data.status ? 'Edit' : 'Set connection'}
                                    </Button>
                                </React.Fragment>
                            ) : (
                                <React.Fragment>
                                    <Button
                                        data-e2e={`integrations_${setupKey}-connection_btn`}
                                        type='link'
                                        padding='0'
                                        onClick={onModalOpen}
                                    >
                                        {data.status ? 'Edit' : 'Set connection'}
                                    </Button>
                                    <StatusBadge setupKey={setupKey} status={data.status} variant={variant} />
                                </React.Fragment>
                            )}
                        </StyledStatus>
                    )}
                </StyledFlex>
            </StyledLine>
        );
    }
);

export default LineItem;
